import type { NodeDefinition } from '../types';

function parseHex(hex: string): [number, number, number, number] {
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map((x) => x + x).join('');
  if (c.length === 6) c += 'ff';
  const num = parseInt(c, 16);
  return [
    (num >> 24) & 255,
    (num >> 16) & 255,
    (num >> 8) & 255,
    num & 255,
  ];
}

function toHex(r: number, g: number, b: number, a = 255): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const hex = [clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('');
  if (a < 255) {
    return `#${hex}${clamp(a).toString(16).padStart(2, '0')}`;
  }
  return `#${hex}`;
}

export const colorMixNode: NodeDefinition = {
  type: 'color.mix',
  category: 'Color',
  label: 'Color Mix',
  description: 'Linearly blends two colors by an interpolation factor',
  inputs: [
    { id: 'a', name: 'Color A', type: 'color', defaultValue: '#0a0a0a' },
    { id: 'b', name: 'Color B', type: 'color', defaultValue: '#b99af8' },
  ],
  outputs: [
    { id: 'result', name: 'Result', type: 'color' },
  ],
  params: [
    { id: 'factor', name: 'Factor', type: 'slider', defaultValue: 0.5, min: 0, max: 1, step: 0.01 },
  ],
  processor: (inputs, params) => {
    const colorA = (inputs.a as string) || '#0a0a0a';
    const colorB = (inputs.b as string) || '#b99af8';
    const factor = Math.max(0, Math.min(1, Number(params.factor ?? 0.5)));

    try {
      const [r1, g1, b1, a1] = parseHex(colorA);
      const [r2, g2, b2, a2] = parseHex(colorB);

      const r = r1 + (r2 - r1) * factor;
      const g = g1 + (g2 - g1) * factor;
      const b = b1 + (b2 - b1) * factor;
      const a = a1 + (a2 - a1) * factor;

      return { result: toHex(r, g, b, a) };
    } catch {
      return { result: colorB };
    }
  },
};
